import React, { useState, useEffect } from 'react';
import { Button, Spinner, Alert as ReactAlert } from 'reactstrap';
import { IconButton, Stack, Tooltip, TextField, InputAdornment } from '@mui/material';
import {
    Search as SearchIcon,
    Replay as ReplayIcon,
    EditNote as EditNoteIcon
} from '@mui/icons-material';
import { loadFileData } from '../../utils/fileUtils';
import { AI_UTILITIES } from '../../constants/aiUtilities';
import { useTranscribeApi } from '../../helpers/ai/transcribe/TranscribeApi';
import { useDiarizeApi } from '../../helpers/ai/diarize/DiarizeApi';
import { useRetellingApi } from '../../helpers/ai/retelling/RetellingApi';
import { useSummarizeResumeApi } from '../../helpers/ai/summarizeResume/SummarizeResumeApi';
import { useUniversalSummarizeResumeApi } from '../../helpers/ai/universalSummarizeResume/UniversalSummarizeResumeApi';
import { useDepersonalizeFileDataApi } from '../../helpers/ai/depersonalizeFileData/DepersonalizeFileDataApi';
import { useStandardControlPSAApi } from '../../helpers/ai/standardControlPSA/StandardControlPSAApi';
import { useMarketPriceAnalysisApi } from '../../helpers/ai/marketPriceAnalysis/MarketPriceAnalysisApi';
import { useInvoiceRecognizerApi } from '../../helpers/ai/InvoiceRecognizer/InvoiceRecognizerApi';
import './UserResults.css';
import { del, get } from '../hooks/apiFetch';
import { useNotification } from '../Notification/NotificationContext';
import { useMountedRef } from '../hooks/useMountedRef';

const FailedResultsTab = ({
    failedItems,
    setFailedItems,
    loading,
    error,
    fetchFailed,
    history,
    getFileIcon
}) => {
    const [failedResults, setFailedResults] = useState(failedItems);
    const [filteredResults, setFilteredResults] = useState([]);
    const [deletingItems, setDeletingItems] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const { submitTranscribeRequest } = useTranscribeApi();
    const { submitDiarizeRequest } = useDiarizeApi();
    const { submitRetellingRequest } = useRetellingApi();
    const { submitUniversalSummarizeResumeRequest } = useUniversalSummarizeResumeApi();
    const { submitSummarizeResumeRequest } = useSummarizeResumeApi();
    const { submitDepersonalizeFileDataRequest } = useDepersonalizeFileDataApi();
    const { submitStandardControlPSARequest } = useStandardControlPSAApi();
    const { submitMarketPriceAnalysisRequest } = useMarketPriceAnalysisApi();
    const { submitInvoiceRecognizerRequest } = useInvoiceRecognizerApi();
    const {
        showSuccess,
        showError
    } = useNotification();
    const isMounted = useMountedRef();

    const formatDateTimeWithLineBreak = (date) => {
        if (!date) return '';
        const formattedDate = date.toLocaleDateString();
        const formattedTime = date.toLocaleTimeString();
        return (
            <div className="date-time-container">
                <div className="date-part">{formattedDate}, {formattedTime}</div>
            </div>
        );
    };

    useEffect(() => {
        setFailedResults(failedItems);
    }, [failedItems]);

    useEffect(() => {
        let results = failedResults;

        if (searchTerm) {
            const lowercasedSearch = searchTerm.toLowerCase();
            results = failedResults.filter(item =>
                (item.id && item.id.toString().toLowerCase().includes(lowercasedSearch)) ||
                item.utility.toLowerCase().includes(lowercasedSearch) ||
                (item.originalFileNames && item.originalFileNames.toString().toLowerCase().includes(lowercasedSearch)) ||
                (item.errorMessage && item.errorMessage.toString().toLowerCase().includes(lowercasedSearch))
            );
        }

        if (sortConfig.key) {
            results = [...results].sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'date') {
                    aValue = aValue.getTime();
                    bValue = bValue.getTime();
                }

                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        setFilteredResults(results);
    }, [failedResults, searchTerm, sortConfig]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <span className="material-symbols-outlined sort-icon">unfold_more</span>;
        }
        return sortConfig.direction === 'asc'
            ? <span className="material-symbols-outlined sort-icon">arrow_upward</span>
            : <span className="material-symbols-outlined sort-icon">arrow_downward</span>;
    };

    const fetchDeleteTask = async (inputIdentifier) => {
        await del(`/api/Task/DeleteTask/${encodeURIComponent(inputIdentifier)}`);
        showSuccess('Обработка успешно удалена');
    };

    const handleDelete = async (inputIdentifier, role) => {
        let windowText;

        windowText = 'Вы уверены, что хотите удалить эту запись?'

        if (!window.confirm(windowText)) {
            return;
        }

        setDeletingItems(prev => new Set(prev).add(inputIdentifier));

        try {
            await fetchDeleteTask(inputIdentifier);

            setFailedItems(prev => prev.filter(item => item.inputIdentifier !== inputIdentifier));
        } catch (error) {
            console.error('Ошибка при удалении:', error);
            showError(error.message || 'Произошла ошибка при удалении');
        } finally {
            setDeletingItems(prev => new Set([...prev].filter(identifier => identifier !== inputIdentifier)));
        }
    };

    const getTaskData = async (item) => {
        const taskDetails = await get(`/api/OperationInfo/GetTaskDetails/${encodeURIComponent(item.inputIdentifier)}`);

        // Преобразуем fileExtensionMap в формат, который ожидают компоненты
        // fileExtensionMap приходит с бэка как { "файлу.mp3": ".txt" }
        const originalFileNames = Object.keys(taskDetails.fileExtensionMap || {});

        // Создаем маппинг имени файла без расширения к расширению результата
        const extensionMap = {};
        Object.entries(taskDetails.fileExtensionMap || {}).forEach(([fileName, ext]) => {
            const fileNameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
            extensionMap[fileNameWithoutExt] = ext.replace('.', ''); // убираем точку
        });
        return { originalFileNames, extensionMap, taskDetails };
    };


    const handleRepeatProcessingWithEditing = async (item) => {
        try {
            // Получаем детальную информацию о задаче из БД
            const { originalFileNames, extensionMap, taskDetails } = await getTaskData(item);

            // Определяем тип утилиты и перенаправляем на соответствующий лоадер
            let loaderPath = '';
            let stateData = {};
            switch (item.utility) {
                case AI_UTILITIES.TRANSCRIBE.name:
                    loaderPath = AI_UTILITIES.TRANSCRIBE.path;
                    stateData = {
                        oldInputIdentifier: item.inputIdentifier,
                        originalFileNames: originalFileNames, // массив путей к файлам
                        extensionMap: extensionMap,
                        comment: taskDetails.comment || '',
                        isRepeatProcessing: true
                    };
                    break;

                case AI_UTILITIES.DIARIZE.name:
                    loaderPath = AI_UTILITIES.DIARIZE.path;
                    stateData = {
                        oldInputIdentifier: item.inputIdentifier,
                        maxCountSpeakers: taskDetails.maxCountSpeakers,
                        originalFileNames: originalFileNames,
                        extensionMap: extensionMap,
                        comment: taskDetails.comment || '',
                        isRepeatProcessing: true
                    };
                    break;

                case AI_UTILITIES.SUMMARIZE_RESUME.name:
                    loaderPath = AI_UTILITIES.SUMMARIZE_RESUME.path;
                    stateData = {
                        oldInputIdentifier: item.inputIdentifier,
                        originalFileNames: originalFileNames,
                        extensionMap: extensionMap,
                        comment: taskDetails.comment || '',
                        isRepeatProcessing: true
                    };
                    break;

                case AI_UTILITIES.UNIVERSAL_SUMMARIZE.name:
                    loaderPath = AI_UTILITIES.UNIVERSAL_SUMMARIZE.path;
                    stateData = {
                        oldInputIdentifier: item.inputIdentifier,
                        originalFileNames: originalFileNames,
                        extensionMap: extensionMap,
                        comment: taskDetails.comment || '',
                        promptInfo: taskDetails.promptInfo,
                        isRepeatProcessing: true
                    };
                    break;

                case AI_UTILITIES.DEPERSONALIZE.name:
                    loaderPath = AI_UTILITIES.DEPERSONALIZE.path;
                    stateData = {
                        oldInputIdentifier: item.inputIdentifier,
                        originalFileNames: originalFileNames,
                        extensionMap: extensionMap,
                        comment: taskDetails.comment || '',
                        isRepeatProcessing: true
                    };
                    break;

                case AI_UTILITIES.RETELLING.name:
                    loaderPath = AI_UTILITIES.RETELLING.path;
                    stateData = {
                        oldInputIdentifier: item.inputIdentifier,
                        originalFileNames: originalFileNames,
                        extensionMap: extensionMap,
                        comment: taskDetails.comment || '',
                        isRepeatProcessing: true
                    };
                    break;

                case AI_UTILITIES.STANDARD_CONTROL_PSA.name:
                    loaderPath = AI_UTILITIES.STANDARD_CONTROL_PSA.path;
                    stateData = {
                        oldInputIdentifier: item.inputIdentifier,
                        originalFileNames: originalFileNames,
                        extensionMap: extensionMap,
                        promptText: taskDetails.promptText,
                        comment: taskDetails.comment || '',
                        isRepeatProcessing: true
                    };
                    break;

                case AI_UTILITIES.MARKET_PRICE_ANALYSIS.name:
                    loaderPath = AI_UTILITIES.STANDARD_CONTROL_PSA.path;
                    stateData = {
                        oldInputIdentifier: item.inputIdentifier,
                        originalFileNames: originalFileNames,
                        extensionMap: extensionMap,
                        promptText: taskDetails.promptText,
                        comment: taskDetails.comment || '',
                        isRepeatProcessing: true
                    };
                    break;

                case AI_UTILITIES.INVOICE_RECOGNIZER.name:
                    loaderPath = AI_UTILITIES.INVOICE_RECOGNIZER.path;
                    stateData = {
                        foldInputIdentifier: item.inputIdentifier,
                        originalFileNames: originalFileNames,
                        extensionMap: extensionMap,
                        comment: taskDetails.comment || '',
                        isRepeatProcessing: true
                    };
                    break;
                default:
                    throw new Error('Неизвестный тип утилиты');
            }

            // Перенаправляем на лоадер с данными
            history.push({
                pathname: loaderPath,
                state: stateData
            });

        } catch (error) {
            console.error('Ошибка при повторной обработке:', error);
            showError(error.message || 'Произошла ошибка при подготовке повторной обработки');
        }
    };

    const handleRepeatProcessing = async (item) => {
        try {
            // Получаем детальную информацию о задаче из БД
            const { originalFileNames, extensionMap, taskDetails } = await getTaskData(item);

            const filesData = await loadFileData(
                item.inputIdentifier,
                originalFileNames,
                extensionMap
            );

            switch (item.utility) {
                case AI_UTILITIES.TRANSCRIBE.name: {
                    const { id, identifier } = await submitTranscribeRequest(filesData, taskDetails.comment);
                    if (isMounted.current) {
                        history.push({
                            pathname: `${AI_UTILITIES.TRANSCRIBE.path}/${identifier}`,
                            state: {
                                id,
                                fileNames: filesData.map(f => f.file.name),
                                comment: taskDetails.comment ?? ''
                            }
                        });
                    }
                    break;
                }
                case AI_UTILITIES.DIARIZE.name: {
                    const { id, identifier } = await submitDiarizeRequest(filesData, taskDetails.maxCountSpeakers, taskDetails.comment);
                    if (isMounted.current) {
                        history.push({
                            pathname: `${AI_UTILITIES.DIARIZE.path}/${identifier}`,
                            state: {
                                id: id,
                                fileNames: filesData.map(f => f.file.name),
                                comment: taskDetails.comment ?? ''
                            }
                        });
                    }
                    break;
                }
                case AI_UTILITIES.SUMMARIZE_RESUME.name: {
                    const { id, identifier } = await submitSummarizeResumeRequest(filesData, taskDetails.comment);
                    if (isMounted.current) {
                        history.push({
                            pathname: `${AI_UTILITIES.SUMMARIZE_RESUME.path}/${identifier}`,
                            state: {
                                id: id,
                                fileNames: filesData.map(f => f.file.name),
                                comment: taskDetails.comment ?? ''
                            }
                        });
                    }
                    break;
                }
                case AI_UTILITIES.UNIVERSAL_SUMMARIZE.name: {
                    const { id, identifier } = await submitUniversalSummarizeResumeRequest(
                        filesData,
                        taskDetails.promptInfo?.name || '',
                        taskDetails.promptInfo?.prompt || '',
                        taskDetails.promptInfo?.promptGuid,
                        taskDetails.comment ?? '',
                        taskDetails.promptInfo?.version
                    );

                    if (isMounted.current) {
                        history.push({
                            pathname: `${AI_UTILITIES.UNIVERSAL_SUMMARIZE.path}/${identifier}`,
                            state: {
                                id,
                                fileNames: filesData.map(f => f.file.name),
                                promptUsed: !!taskDetails.promptInfo,
                                comment: taskDetails.comment ?? ''
                            }
                        });
                    }
                    break;
                }
                case AI_UTILITIES.DEPERSONALIZE.name: {
                    const { id, identifier } = await submitDepersonalizeFileDataRequest(filesData, taskDetails.comment);
                    if (isMounted.current) {
                        history.push({
                            pathname: `${AI_UTILITIES.DEPERSONALIZE.path}/${identifier}`,
                            state: {
                                id: id,
                                fileNames: filesData.map(f => f.file.name),
                                comment: taskDetails.comment ?? ''
                            }
                        });
                    }
                    break;
                }
                case AI_UTILITIES.RETELLING.name: {
                    const { id, identifier } = await submitRetellingRequest(filesData, taskDetails.comment);
                    if (isMounted.current) {
                        history.push({
                            pathname: `${AI_UTILITIES.RETELLING.path}/${identifier}`,
                            state: {
                                id: id,
                                fileNames: filesData.map(f => f.file.name),
                                comment: taskDetails.comment ?? ''
                            }
                        });
                    }
                    break;
                }
                case AI_UTILITIES.STANDARD_CONTROL_PSA.name: {
                    const { id, identifier } = await submitStandardControlPSARequest(filesData, taskDetails.promptText, taskDetails.comment);
                    if (isMounted.current) {
                        history.push({
                            pathname: `${AI_UTILITIES.STANDARD_CONTROL_PSA.path}/${identifier}`,
                            state: {
                                id: id,
                                fileNames: filesData.map(f => f.file.name),
                                comment: taskDetails.comment ?? ''
                            }
                        });
                    }
                    break;
                }
                case AI_UTILITIES.MARKET_PRICE_ANALYSIS.name: {
                    const { id, identifier } = await submitMarketPriceAnalysisRequest(filesData, taskDetails.promptText, taskDetails.comment);
                    if (isMounted.current) {
                        history.push({
                            pathname: `${AI_UTILITIES.MARKET_PRICE_ANALYSIS.path}/${identifier}`,
                            state: {
                                id: id,
                                fileNames: filesData.map(f => f.file.name),
                                comment: taskDetails.comment ?? ''
                            }
                        });
                    }
                    break;
                }
                case AI_UTILITIES.INVOICE_RECOGNIZER.name: {
                    const { id, identifier } = await submitInvoiceRecognizerRequest(filesData, taskDetails.comment);
                    if (isMounted.current) {
                        history.push({
                            pathname: `${AI_UTILITIES.INVOICE_RECOGNIZER.path}/${identifier}`,
                            state: {
                                id,
                                fileNames: filesData.map(f => f.file.name),
                                comment: taskDetails.comment ?? ''
                            }
                        });
                    }
                    break;
                }
                default:
                    throw new Error('Неизвестный тип утилиты');
            }

        } catch (error) {
            console.error('Ошибка при повторной обработке:', error);
            showError(error.message || 'Произошла ошибка при подготовке повторной обработки');
        }
    };

    const renderFailedResultsTable = () => {
        return (
            <section className="table-responsive mt-0">
                <table className="table table-striped user-results-table">
                    <colgroup>
                        <col style={{ minWidth: '80px' }} />
                        <col style={{ minWidth: '100px' }} />
                        <col style={{ minWidth: '100px' }} />
                        <col style={{ minWidth: '180px' }} />
                        <col style={{ minWidth: '100px' }} />
                        <col style={{ minWidth: '120px' }} />
                        <col style={{ minWidth: '250px' }} />
                        {filteredResults.some(item => item.comment) &&
                            <col style={{ width: '150px' }} />
                        }
                        <col style={{ width: '250px' }} />
                        <col style={{ width: '120px' }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th
                                className="text-left align-middle sortable"
                                onClick={() => handleSort('id')}
                            >
                                <div className="header-content">
                                    <span className="text-data header-text">
                                        Id
                                    </span>
                                    <div className="sort-icon-container">
                                        {getSortIcon('id')}
                                    </div>
                                </div>
                            </th>
                            <th
                                className="text-left align-middle sortable"
                                onClick={() => handleSort('date')}
                            >
                                <div className="header-content">
                                    <span className="text-data header-text">
                                        Дата и время ошибки
                                    </span>
                                    <div className="sort-icon-container">
                                        {getSortIcon('date')}
                                    </div>
                                </div>
                            </th>
                            <th
                                className="text-left align-middle sortable"
                                onClick={() => handleSort('utility')}
                            >
                                <div className="header-content">
                                    <span className="text-data header-text">
                                        Утилита
                                    </span>
                                    <div className="sort-icon-container">
                                        {getSortIcon('utility')}
                                    </div>
                                </div>
                            </th>
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Название промпта
                                </span>
                            </th>
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Владелец
                                </span>
                            </th>
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Ваша Роль
                                </span>
                            </th>
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Исходные файлы
                                </span>
                            </th>
                            {filteredResults.some(item => item.comment) &&
                                <th className="text-left align-middle">
                                    <span className="text-data">
                                        Комментарий
                                    </span>
                                </th>
                            }
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Сообщение об ошибке
                                </span>
                            </th>
                            <th className="text-left align-middle">
                                <span className="text-data">
                                    Действия
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredResults.map(item => (
                            <tr key={item.id}>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {item.id}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {formatDateTimeWithLineBreak(item.date)}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {item.utility}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {item.utility === "Универсальная суммаризация (техническая)"
                                            ? (item.promptName ? item.promptName : "Промпт был удалён")
                                            : "Стандартный промпт"
                                        }
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {item.creator}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <span className="text-data">
                                        {item.roleDisplayName}
                                    </span>
                                </td>
                                <td className="text-left align-middle">
                                    <div className="file-list-container">
                                        {item.originalFileNames
                                            ? item.originalFileNames.map((fileName, index) => (
                                                <div key={index} className="file-item">
                                                    <Tooltip title={fileName.trim()}>
                                                        <div className="file-item-content">
                                                            {getFileIcon(fileName.trim())}
                                                            <span className="file-name text-data full-file-name ml-1">
                                                                {fileName.length > 30 ? `${fileName.substring(0, 30)}…` : fileName}
                                                            </span>
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                            ))
                                            : <p>
                                                Файл не найден
                                            </p>
                                        }
                                    </div>
                                </td>
                                {filteredResults.some(item => item.comment) &&
                                    <td className="text-left align-middle">
                                        <span className="text-data">
                                            {item.comment}
                                        </span>
                                    </td>
                                }
                                <td className="text-left align-middle">
                                    <Tooltip title={item.errorMessage}>
                                        <span className="text-data">
                                            {item.errorMessage.length > 70
                                                ? `${item.errorMessage.substring(0, 70)}…`
                                                : item.errorMessage}
                                        </span>
                                    </Tooltip>
                                </td>
                                <td className="text-left align-middle">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Tooltip title="Повторить обработку">
                                            <IconButton
                                                onClick={(e) => handleRepeatProcessing(item, e)}
                                                onMouseDown={(e) => {
                                                    if (e.button === 1) {
                                                        e.preventDefault();
                                                        handleRepeatProcessing(item, e);
                                                    }
                                                }}
                                                size="small">
                                                <ReplayIcon className="view-result-icon" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Повторить обработку с редактированием">
                                            <IconButton
                                                onClick={(e) => handleRepeatProcessingWithEditing(item, e)}
                                                onMouseDown={(e) => {
                                                    if (e.button === 1) {
                                                        e.preventDefault();
                                                        handleRepeatProcessingWithEditing(item, e);
                                                    }
                                                }}
                                                size="small">
                                                <EditNoteIcon className="view-result-icon" />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Удалить">
                                            <IconButton
                                                onClick={() => handleDelete(item.inputIdentifier, item.role)}
                                                size="small"
                                                disabled={deletingItems.has(item.inputIdentifier)}
                                            >
                                                {deletingItems.has(item.inputIdentifier)
                                                    ? (
                                                        <span className="material-symbols-outlined icon delete-icon">
                                                            auto_delete
                                                        </span>
                                                    )
                                                    : (
                                                        <span className="material-symbols-outlined icon delete-icon">
                                                            delete
                                                        </span>
                                                    )}
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        );
    };

    return (
        <>
            <div className="search-container">
                <TextField
                    fullWidth
                    variant="standard"
                    placeholder="Поиск по id, утилите, файлам, ошибке..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input text-input"
                    autoComplete="off"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon className="search-icon" />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>

            {loading ? (
                <div className="text-center my-5">
                    <Spinner color="primary" />
                    <p>Загрузка неудачных обработок...</p>
                </div>
            ) : error ? (
                <ReactAlert color="danger" transition={{ timeout: 300 }}>
                    <p className="alert-heading">Ошибка при загрузке данных:</p>
                    <p className="black-color">{error}</p>
                    <br />
                    <Button
                        className="dt1520-button active-dt1520-button default-button view-button mt-10"
                        onClick={fetchFailed}>
                        Попробовать снова
                    </Button>
                </ReactAlert>
            ) : filteredResults.length > 0 ? (
                renderFailedResultsTable()
            ) : failedResults.length > 0 ? (
                <ReactAlert color="warning" className="mt-2" transition={{ timeout: 300 }}>
                    По вашему запросу ничего не найдено
                </ReactAlert>
            ) : (
                <ReactAlert color="success" className="mt-2" transition={{ timeout: 300 }}>
                    Нет данных о неудачных обработках - отличный результат!
                </ReactAlert>
            )}
        </>
    );
};

export default FailedResultsTab;

